import { Spacing, Text } from '@vkontakte/vkui';
import React, { type FC, memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { duplicatesLimitCount, duplicatesTariffsModal } from 'reactApp/appHelpers/featuresHelpers/features/duplicates';
import { cdnPackagesUrl } from 'reactApp/appHelpers/themeToolHelpers/default-values';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import { useDuplicatesAnalytic } from 'reactApp/hooks/duplicates/useDuplicatesAnalytic';
import { useTariffBuy } from 'reactApp/modules/payment/hooks/useTariffBuy';
import { openBuyDisableAds } from 'reactApp/modules/payment/payment.module';
import { isCurrentDarkTheme } from 'reactApp/modules/theme/theme.selectors';
import { sendXray } from 'reactApp/utils/ga';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './DuplicatesRemoveLimitModal.css';

interface Props {
    onClose?: () => void;
}

const name = 'dp-rm-lim-modal';

const handleDismiss = () => {
    sendXray([name, 'close']);
};

export const DuplicatesRemoveLimitModal: FC<Props> = memo(function DuplicatesRemoveLimitModal({ onClose }: Props) {
    const isDarkTheme = useSelector(isCurrentDarkTheme);
    const analyticData = useDuplicatesAnalytic();
    const dispatch = useDispatch();

    const onClickBuy = useTariffBuy({
        paySource: duplicatesTariffsModal.paySource,
    });

    const handleClose = useCallback(() => {
        onClose?.();
    }, [onClose]);

    const handleShow = useCallback(() => {
        sendXray([name, 'show']);

        sendPaymentGa({
            eventCategory: ECategoryGa.duplicates,
            action: 'modal-show',
            modal_type: 'remove-limit',
            ...analyticData,
        });
    }, [analyticData]);

    const handleClick = useCallback(() => {
        sendXray([name, 'click']);

        sendPaymentGa({
            eventCategory: ECategoryGa.duplicates,
            action: 'modal-click',
            modal_type: 'remove-limit',
            action_type: 'try-promo',
            ...analyticData,
        });

        handleClose();
        dispatch(
            openBuyDisableAds({
                title: duplicatesTariffsModal.buyTitle,
                tariffsNumber: duplicatesTariffsModal.tariffsNumber,
                activeTab: duplicatesTariffsModal.activeTab,
                isFrame: false,
                onBuyClick: onClickBuy,
                actionShow: duplicatesTariffsModal.actionShow,
                actionClick: duplicatesTariffsModal.actionClick,
            })
        );
    }, [analyticData, handleClose, dispatch, onClickBuy]);

    return (
        <WhatsNewDialog
            imageUrl={`${cdnPackagesUrl}/static/cloud/promo/duplicates/promo-modal-${isDarkTheme ? 'dark' : 'light'}.png`}
            imageWrapperClassName={styles.imageWrapper}
            contentClassName={styles.content}
            title={`Вы\u00A0удалили дубликаты ${duplicatesLimitCount}\u00A0фото, дальше нужна подписка`}
            content={
                <>
                    <Spacing size={16} />
                    <Text data-qa-id="text">
                        Можно удалить все&nbsp;&mdash; с&nbsp;подпиской Mail&nbsp;Space. И&nbsp;место освободится, и&nbsp;фото
                        не&nbsp;придётся разбирать вручную
                    </Text>
                    <Spacing size={32} />
                </>
            }
            primaryButtonText="Попробовать подписку"
            closeAfterClick
            onClick={handleClick}
            onClose={handleClose}
            onShow={handleShow}
            onDismiss={handleDismiss}
            imageBgColor="var(--vkui--color_background_modal)"
            buttonTheme="vk"
            dialogSize="small"
            qaId="duplicates-remove-limit-modal"
            isRebranding
        />
    );
});

DuplicatesRemoveLimitModal.displayName = 'DuplicatesRemoveLimitModal';
